import React from "react";
import { memoize } from "lodash/function";
import { store } from "@store";
import locale from "@bonliva-crm/locale";
import { constants } from "@bonliva-crm/common";
import {
  CustomerType,
  LegalPersonTypeWithNoOption,
  OrganizationType,
  UserSelectFragment
} from "@graphql";
import { HasBookingKitsFilter } from "@bonliva-crm/shared";
import ConsultantSelector from "@containers/VirtualSelector/ConsultantSelector/ConsultantSelector";
import { Consultant } from "@containers/VirtualSelector/ConsultantSelector";
import CustomerSelector from "@containers/VirtualSelector/CustomerSelector/CustomerSelector";
import { Customer } from "@containers/VirtualSelector/CustomerSelector";
import { NestedExpertiseSelect } from "@containers/NestedExpertiseSelect/NestedExpertiseSelect";
import { ProjectMultiSelect } from "@containers";
import { Option } from "@components/Select";
import { Grid3 } from "./Grid3";
import { SearchSelect } from "@components/Select/SearchSelect";
import {
  NoCustomerGroupOption,
  NoCustomerGroupOption1
} from "@customers/CustomerSearch/utils";
import { SearchFilter } from "@containers/VirtualSelector/CustomerSelector/CustomerSelectorStore";
import { usersToOptions } from "@search/GeneralSearchStore";

export const memoizedGetOptions = memoize((data) => {
  const parentOptions = data.filter(
    // eslint-disable-next-line no-prototype-builtins
    (item) => !item.hasOwnProperty("parent") || !item.parent
  );
  // exclude sub-options that has {parent} that is equal to it's {value}
  const withoutDuplications = data.filter(
    // eslint-disable-next-line no-prototype-builtins
    (item) => !item.hasOwnProperty("parent") || item.parent !== item.value
  );
  // add {children} property to every parent
  const filledParentOptions = parentOptions.map((parent) => {
    const parentSubOptions = withoutDuplications
      // find only sub-options for current parent
      .filter((subOption) => subOption.parent === parent.value);
    return {
      ...parent,
      children: parentSubOptions
    };
  });

  let options = [];
  filledParentOptions.forEach((item) => {
    options = [...options, item, ...item.children];
  });

  return options;
});

export function renderConsultantSelector(
  onChange: (v: Consultant[]) => void,
  value: LabelValue[],
  dirty: boolean,
  selectorProps?: Partial<React.ComponentProps<typeof ConsultantSelector>>
) {
  return (
    <Grid3 dirty={dirty}>
      <ConsultantSelector
        fullWidth
        slim
        multi
        onChange={onChange}
        value={value}
        {...(selectorProps as any)}
      />
    </Grid3>
  );
}

export function renderCustomerSelector(
  onChange: (v: Customer[]) => void,
  value: LabelValue[],
  dirty: boolean,
  selectorProps?: Partial<React.ComponentProps<typeof CustomerSelector>>,
  initialFilter: Partial<SearchFilter> = {
    customerType: CustomerType.Hospital
  }
) {
  return (
    <Grid3 dirty={dirty}>
      <CustomerSelector
        fullWidth
        slim
        multi
        onChange={onChange}
        value={value}
        initialFilter={initialFilter}
        {...(selectorProps as any)}
      />
    </Grid3>
  );
}

export function renderUsers(
  users: UserSelectFragment[],
  label: string,
  value: string[],
  onChange: (v: string[]) => void,
  dirty: boolean,
  disabled?: boolean,
  includeEmpty?: boolean,
  includeAny?: boolean
) {
  const myProfile = store().user.myProfile;

  let options: Option[] = store().user.can("user:list-all:all")
    ? usersToOptions(users)
    : usersToOptions(
        // Show only users from my teams
        users.filter((user) => user.Teams.some((team) => myProfile.Teams.includes(team)))
      );

  if (options.length === 0) {
    options.push({
      label: store().user.name,
      value: store().user.id
    });
  }

  if (includeEmpty) {
    options = [
      {
        label: locale.ResponsiblePersonEmpty,
        value: constants.EMPTY_RESPONSIBLE_PERSON
      },
      ...options
    ];
  }

  if (includeAny) {
    options.unshift({
      label: locale.ResponsiblePersonAny,
      value: constants.ANY_RESPONSIBLE_PERSON
    });
  }

  return (
    <Grid3 dirty={dirty}>
      <SearchSelect
        label={label}
        options={options}
        value={value}
        onChange={onChange}
        sortOptions={false}
        data-test="responsiblePerson"
        disabled={disabled}
      />
    </Grid3>
  );
}

export function renderProjectSelector(
  value: string[],
  onChange: (v: string[]) => void,
  dirty: boolean,
  disabled?: boolean
) {
  return (
    <Grid3 dirty={dirty}>
      <ProjectMultiSelect
        fullWidth
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    </Grid3>
  );
}

export function renderSpecialization(
  value: string[],
  onChange: (v: string[]) => void,
  dirty: boolean,
  disabled?: boolean
) {
  return (
    <Grid3 dirty={dirty}>
      <SearchSelect
        data-test="specialization"
        disabled={disabled}
        label={locale.Specialization}
        onChange={onChange}
        options={store().generalSearchStore.allSpecializations}
        value={value}
      />
    </Grid3>
  );
}

export function renderRegions(
  value: string[],
  onChange: (v: string[]) => void,
  dirty: boolean,
  renderLocaleRegion = false,
  extraRegions: Option[] = []
) {
  const options = memoizedGetOptions(store().generalSearchStore.regions)
    .concat(extraRegions)
    .sortBy((x) => x.label);

  return (
    <Grid3 dirty={dirty}>
      <SearchSelect
        label={renderLocaleRegion ? locale.CanWorkInRegion : locale.Region}
        options={options}
        sortGroups={false}
        sortOptions={false}
        value={value}
        onChange={onChange}
        grouped
      />
    </Grid3>
  );
}

export function renderCustomerTypeSelect(
  value: CustomerType[],
  onChange: (v: CustomerType[]) => void,
  disabled: boolean,
  dirty: boolean
) {
  const options: Option[] = [];

  const groups = [
    NoCustomerGroupOption,
    ...store().cache.customerGroups.map((g) => ({
      label: g,
      value: g,
      parent: CustomerType.Hospital
    }))
  ];

  // For correct order
  options.push({
    label: locale.customerType[CustomerType.Region],
    value: CustomerType.Region
  });
  options.push({
    label: locale.customerType[CustomerType.Hospital],
    value: CustomerType.Hospital,
    children: groups
  });
  options.push(...groups);
  options.push({
    label: locale.customerType[CustomerType.Department],
    value: CustomerType.Department
  });

  return (
    <Grid3 dirty={dirty}>
      <SearchSelect
        grouped
        label={locale["Customer type"]}
        sortGroups={false}
        sortOptions={false}
        options={options}
        value={value}
        onChange={(args) => {
          onChange(args);
        }}
        disabled={disabled}
      />
    </Grid3>
  );
}

export function renderCustomerTypeSelectHospitalOnly(
  value: string[],
  onChange: (v: string[]) => void,
  disabled: boolean,
  dirty: boolean
) {
  const options: Option[] = [];

  const groups = [
    NoCustomerGroupOption1,
    ...store().cache.customerGroups.map((g) => ({
      label: g,
      value: g
    }))
  ];

  options.push(...groups);

  return (
    <Grid3 dirty={dirty}>
      <SearchSelect
        grouped
        label={locale["Customer type"]}
        sortGroups={false}
        sortOptions={false}
        options={options}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    </Grid3>
  );
}

export function renderProfessions(
  value: string[],
  onChange: (v: string[]) => void,
  dirty: boolean,
  disabled: boolean
) {
  return (
    <Grid3 dirty={dirty}>
      <NestedExpertiseSelect disabled={disabled} onChange={onChange} value={value} />
    </Grid3>
  );
}

export function renderOrgTypeSelect(
  value: OrganizationType[],
  onChange: (v: OrganizationType[]) => void,
  disabled: boolean,
  dirty: boolean
) {
  return (
    <Grid3 dirty={dirty}>
      <SearchSelect
        label={locale.OrganizationType}
        value={value}
        options={Object.values(OrganizationType).toLabelValue(
          (t) => locale.organizationType[t]
        )}
        onChange={onChange}
        disabled={disabled}
      />
    </Grid3>
  );
}

export function renderWorkRequestPriority(
  value: string,
  options: any[],
  onChange: (v: any) => void,
  dirty: boolean,
  disabled: boolean
) {
  return (
    <Grid3 dirty={dirty}>
      <SearchSelect
        label={locale.tickets.priority}
        options={options}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    </Grid3>
  );
}

export function renderAOESelector(
  onChange: (v: string[]) => void,
  value: string[],
  disabled?: boolean,
  withExclusive?: boolean
) {
  const aoe = withExclusive
    ? store().generalSearchStore.areasOfExpertiseWithExclusiveOptions
    : store().generalSearchStore.areasOfExpertiseOptions;

  const options = memoizedGetOptions(aoe);

  // console.log(toJS(options.filter(x => !x.parent)));
  return (
    <SearchSelect
      disabled={disabled}
      grouped
      label={locale.Expertise}
      onChange={onChange}
      options={options}
      sortOptions={[]}
      value={value}
    />
  );
}

export function renderAOE(
  onChange: (v: string[]) => void,
  value: string[],
  dirty: boolean,
  disabled?: boolean,
  withExclusive?: boolean
) {
  return (
    <Grid3 dirty={dirty}>
      {renderAOESelector(onChange, value, disabled, withExclusive)}
    </Grid3>
  );
}

export function renderLegalPersonType(
  value: LegalPersonTypeWithNoOption,
  onChange: (v: LegalPersonTypeWithNoOption) => void,
  dirty: boolean
) {
  return (
    <Grid3 dirty={dirty}>
      <SearchSelect
        label={locale["Employment type"]}
        options={Object.values(LegalPersonTypeWithNoOption).toLabelValue(
          (w) => locale.legalPersonTypeWithNoOption[w]
        )}
        value={value}
        onChange={onChange}
      />
    </Grid3>
  );
}

export function renderNationalProcurement(
  value: string,
  onChange: (v: string) => void,
  dirty: boolean
) {
  return (
    <Grid3 dirty={dirty}>
      <SearchSelect
        label={locale.NationalProcurement}
        options={[
          {
            label: locale.Yes,
            value: "true"
          },
          {
            label: locale.No,
            value: "false"
          }
        ]}
        value={value}
        onChange={onChange}
      />
    </Grid3>
  );
}

export function renderHasBookingKits(
  value: string,
  onChange: (v: string) => void,
  dirty: boolean
) {
  return (
    <Grid3 dirty={dirty}>
      <SearchSelect
        label={locale.hasBookingKit}
        options={[
          {
            label: locale.Yes,
            value: HasBookingKitsFilter.True
          },
          {
            label: locale.No,
            value: HasBookingKitsFilter.False
          },
          {
            label: locale.hasBookingKitMissingCompleteAndAccruedCost,
            value: HasBookingKitsFilter.MissingCompleteAndAccruedCost
          },
          {
            label: locale.hasBookingKitMissingCompleteAndAccruedIncome,
            value: HasBookingKitsFilter.MissingCompleteAndAccruedIncome
          }
        ]}
        value={value}
        onChange={onChange}
      />
    </Grid3>
  );
}

export function renderHasAccommodationBenefitTax(
  value: string,
  onChange: (v: string) => void,
  dirty: boolean
) {
  return (
    <Grid3 dirty={dirty}>
      <SearchSelect
        label={locale.hasAccommodationBenefitTax}
        options={[
          {
            label: locale.Yes,
            value: "true"
          },
          {
            label: locale.No,
            value: "false"
          }
        ]}
        value={value}
        onChange={onChange}
      />
    </Grid3>
  );
}
